import { ArrowSmRightIcon } from '@heroicons/react/solid';
import { HalfScreen, SignInButtons } from 'components';
import { PageProps } from 'components/Auth';
import HalfScreenResponsive from 'components/HalfScreen/HalfScreenResponsive';
import cookieCutter from 'cookie-cutter';
import { GetServerSidePropsResult } from 'next';
import { Provider } from 'next-auth/providers';
import { getProviders, signIn, useSession, signOut } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import { isClient } from 'utils';

export default function SignUp({ providers }: { providers: { [key: string]: Provider } }): ReactElement {
  const { status: sessionStatus } = useSession();
  const router = useRouter();

  // TODO: Need to attach this to the new user
  const { invite } = router.query;

  const steps = [
    { name: 'signup', status: 'current' },
    { name: 'company', status: '' },
    { name: 'teams', status: '' },
    { name: 'users', status: '' },
  ];

  let redirectUrl = '/';
  if (isClient()) {
    // Function to clear all cookies
    const clearAllCookies = () => {
      const cookies = document.cookie.split(';');

      for (let i = 0; i < cookies.length; i++) {
        const cookieName = cookies[i].split('=')[0].trim();
        cookieCutter.set(cookieName, '', { expires: new Date(0) });
      }
    };

    redirectUrl = cookieCutter.get('redirectUrl');
    console.log({ redirectUrl });

    // If already logged in as another user sign out and clear localstorage and clache
    if (invite && sessionStatus === 'authenticated') {
      signOut();
      // clearAllCookies();
      localStorage.removeItem('team');
      localStorage.setItem('s2-invite-token', invite as string);
    } else if (sessionStatus === 'authenticated') {
      router.push(redirectUrl || '/company/all');
      // router.push('/company/all');
    } else if (invite) {
      localStorage.setItem('s2-invite-token', invite as string);
    }
  }
  const removedCompany = router.query['remove'];

  return (
    <HalfScreenResponsive
      image={{
        src: '/sign-up.png',
      }}
    >
      <div>
        <Image
          src="/system-and-soul-logo.svg"
          height={50}
          width={100}
          alt="Logo"
          style={{
            maxWidth: '100%',
            height: 'auto',
          }}
        />
        <nav className="flex items-center mt-5" aria-label="Progress">
          <p className="text-sm font-medium">
            Step {steps.findIndex((step) => step.status === 'current') + 1} of {steps.length}
          </p>
          <ol role="list" className="ml-8 flex items-center space-x-5">
            {steps.map((step) => (
              <li key={step.name}>
                {step.status === 'current' ? (
                  <div className="relative flex items-center justify-center" aria-current="step">
                    <span className="absolute w-5 h-5 p-px flex" aria-hidden="true">
                      <span className="w-full h-full rounded-full bg-primary-200" />
                    </span>
                    <span className="relative block w-2.5 h-2.5 bg-primary-600 rounded-full" aria-hidden="true" />
                    <span className="sr-only">{step.name}</span>
                  </div>
                ) : (
                  <div className="block w-2.5 h-2.5 bg-gray-200 rounded-full">
                    <span className="sr-only">{step.name}</span>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </nav>
        <h2 className="mt-6 text-2xl font-semibold text-gray-900 tracking-tighter">Try System & Soul for free</h2>
        <p className="text-sm mt-2">
          Already have an account?
          <Link href="/auth/signin" passHref legacyBehavior>
            <span className="inline text-primary cursor-pointer">
              {` Sign in`}
              <ArrowSmRightIcon className="h-5 w-5 inline" />
            </span>
          </Link>
        </p>
      </div>
      <SignInButtons
        providers={providers}
        label="Sign up"
        authorizationParams={{ prompt: 'login' }}
        onClickButton={(providerId, options, authorizationParams) => {
          if (invite) {
            localStorage.setItem('s2-invite-token', invite as string);
          }
          console.log({ invite });
          console.log({ redirectUrl });
          console.log(process.env.NEXT_PUBLIC_VERCEL_URL, 'VERCEL URL');
          signIn(
            providerId,
            {
              ...options,
              // TODO: Why isn't this working?
              callbackUrl: invite ? (redirectUrl ? redirectUrl : '/company/all') : process.env.NEXT_PUBLIC_VERCEL_URL,
            },
            authorizationParams,
          );
        }}
      />
      {router.query['company-exist'] ? (
        <div className="mt-4 text-center  border border-transparent rounded-md shadow-sm bg-signup-yellow">
          <p className="text-sm font-semibold py-1">
            This company name is already registered. Please login into continue or contact
            <span className="text-blue-500 ml-1">help@systemandsoul.com</span>
          </p>
        </div>
      ) : null}
      {removedCompany ? (
        <div className="mt-4 text-center  border border-transparent rounded-md shadow-sm bg-signup-yellow">
          <p className="text-sm font-semibold py-1">
            You have been removed from {removedCompany}. Please login with new company or contact
            <span className="text-blue-500 ml-1">help@systemandsoul.com</span>
          </p>
        </div>
      ) : null}
    </HalfScreenResponsive>
  );
}

// This is the recommended way for Next.js 9.3 or newer
export async function getServerSideProps(): Promise<GetServerSidePropsResult<PageProps>> {
  const providers = await getProviders();

  return {
    props: {
      providers,
      pageTitle: 'Sign Up',
      hideSidebar: true,
    },
  };
}
